import { dynamicApi } from '../../services/api';
import { formatDateOnly } from '../../utils/dateTime';

/**
 * Assembles the data each transmittal PDF needs, from the project database.
 *
 * Submittal Transmittal: buildTransmittalPdfData(id), below.
 *
 * Three reads, because the form spans three tables: the transmittal itself, the recipient
 * company (its address lives on Transfer_Party, so a reprint shows the company's CURRENT
 * address - the deliberate trade against snapshotting it per transmittal), and the line items.
 *
 * Field names come back camel-cased from the dynamic API, and FK columns carry a companion
 * "<field>_Display" holding the label.
 */

// Transfer_Date is a date column: it prints literally, never zone-shifted.
const dateOnly = formatDateOnly;

export async function buildTransmittalPdfData(transmittalId) {
  const { data: t } = await dynamicApi.getById('Transmittal_Log', transmittalId);
  if (!t) throw new Error(`Transmittal ${transmittalId} not found.`);

  // Recipient address. Guarded rather than assumed - a transmittal with no transfer party is
  // valid data, and the form should print blank lines rather than fail to render.
  let party = null;
  if (t.transfer_Party) {
    try {
      const res = await dynamicApi.getById('Transfer_Party', t.transfer_Party);
      party = res.data || null;
    } catch { /* leave the To: block blank rather than lose the whole document */ }
  }

  let source = null;
  if (t.source) {
    try {
      const res = await dynamicApi.getById('Source', t.source);
      source = res.data || null;
    } catch { /* same reasoning */ }
  }

  let items = [];
  try {
    const res = await dynamicApi.getFiltered('Transmittal_Item', 'Transmittal_Log', transmittalId);
    items = (res.data || [])
      .sort((a, b) => (a.item_Order ?? 0) - (b.item_Order ?? 0))
      .map(i => ({
        clientSpec:    i.client_Spec || '',
        specPara:      i.spec_Paragraph || '',
        description:   i.description || '',
        manufacturer:  i.manufacturer || '',
        submittalType: i.submittal_Type || '',
        leadTime:      i.lead_Time || '',
      }));
  } catch { /* an itemless transmittal still prints, with 11 ruled blank rows */ }

  return {
    transmittalNumber: t.transmittal_Number || '',
    revision:          t.revision || 'A',

    projectName:   source?.project_Desc || '',
    projectNumber: source?.project_Number || '',

    to:      t.transfer_Party_Display || party?.transfer_Party || '',
    toAttn1: party?.address_Line_1 || '',
    toAttn2: party?.address_Line_2 || '',
    toAttn3: party?.attn_Name || '',

    // The From: block is the WM office running the project. It varies by office (Addison TX
    // on the example transmittal, Malta NY on the ISA datasheets) and has no home in the
    // schema yet, so it prints blank rather than being hardcoded to one office.
    from: '', fromAttn1: '', fromAttn2: '', fromAttn3: '',

    dateSubmitted:   dateOnly(t.transfer_Date),
    // The form's "Subcontractor Submittal No." is our transmittal number.
    submittalNumber: t.transmittal_Number || '',
    // Phase/Building/Package comes from Source. Which column is still open: the example reads
    // "Manufacturing Building A", which is neither Project_Number nor Project_Desc.
    packageNumber:   source?.project_Scope || '',
    quantityRequired: t.qty_Required || '',
    quantityActual:   t.qty_Actual || '',
    resubmission:     t.resubmission || '',

    submittedForReview:   !!t.chk_Submitted_Review,
    resubmittedForReview: !!t.chk_Resubmitted_Review,
    substitutionInvolved: !!t.chk_Substitution,
    resubmittedForRecord: !!t.chk_Resubmitted_Record,
    otherLeft:            !!t.chk_Other_Left,
    leedSubmittal:        !!t.chk_LEED,
    fullSubmittal:        !!t.chk_Full_Submittal,
    partialSubmittal:     !!t.chk_Partial_Submittal,
    otherRight:           !!t.chk_Other_Right,

    // Signatures are not in the schema. The form prints the ruled lines either way, which is
    // what a document signed by hand after printing needs.
    receivingSignature: '', receivingDate: '',
    transferringSignature: '', transferringDate: '',

    items,
  };
}

// Source carries the project's title and number for the RFI and Letter of Transmittal
// headers. A missing or unreadable Source prints blank rather than failing the document.
const readSource = async (sourceId) => {
  if (!sourceId) return null;
  try {
    const res = await dynamicApi.getById('Source', sourceId);
    return res.data || null;
  } catch {
    return null;
  }
};

/**
 * Data for the Request For Information form. Field mapping: RFI Number = Transmittal_Number,
 * Date = Transfer_Date, Respondent = Transfer_Party, Project Title / Number from Source, and
 * Client from the project database's organisation, which the caller passes in.
 */
export async function buildRfiPdfData(row, { client = '' } = {}) {
  const source = await readSource(row.source);
  return {
    transmittalNumber:    row.transmittal_Number || '',
    projectTitle:         source?.project_Desc || '',
    projectNumber:        source?.project_Number || '',
    client,
    owner:                row.owner || '',
    date:                 dateOnly(row.transfer_Date),
    discipline:           row.discipline || '',
    buildingArea:         row.building_Area || '',
    reference:            row.reference || '',
    respondent:           row.transfer_Party_Display || '',
    author:               row.author || '',
    informationRequested: row.information_Requested || '',
    proposedSolution:     row.proposed_Solution || '',
    response:             row.response || '',
    attachmentUrl:        row.attachment_Url || '',
    impact:               row.impact || '',
  };
}

/**
 * Data for the Letter of Transmittal. `items` are the live Tag and Panel rows carrying this
 * transmittal's number in Transfer_No, already shaped { tag, description, serial }.
 * The From: office has no home in the schema yet and prints blank.
 */
export async function buildLetterOfTransmittalPdfData(row, items = []) {
  const source = await readSource(row.source);
  return {
    transmittalNumber: row.transmittal_Number || '',
    projectTitle:      source?.project_Desc || '',
    projectNumber:     source?.project_Number || '',
    to:                row.transfer_Party_Display || '',
    from:              '',
    date:              dateOnly(row.transfer_Date),
    sendingType:       row.sending_Type || '',
    items,
  };
}

export default buildTransmittalPdfData;
